import React, { useEffect, useState } from "react";
import { Loading } from "../../../common/components/Loading";
import { useActionRequest, useDataRequest } from "../../../common/hooks/api";
import { NetworkRequest } from "../../../common/models";
import { Api } from "../../../common/service/Api";
import {
  LoadingPageWrapper,
  StandardPageWrapper,
} from "../../components/layout/StandardPageWrapper";
import { usePrimaryOrganizationId } from "../../hooks/useOrganization";
import { TopNavigationBreadcrumb } from "../../components/privacyCenter/TopNavigationBreadcrumb";
import clsx from "clsx";
import { AlertCard, Button, EmailMessage, Slideout } from "@truevault/aquilo";
import { Tooltip } from "@mui/material";
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";

const OMITTED_EMAIL_TEMPLATE_SLUGS = ["extend-due-date", "closed-opt-out"];
const DELETE_EMAIL_VERIFICATION_TEMPLATE_SLUG = "delete-email-verification";

export const EmailTemplateSettings = () => {
  const organizationId = usePrimaryOrganizationId();

  const { result: messageTypes, request } = useDataRequest({
    queryKey: ["messageTypes", organizationId],
    api: () => Api.organization.getOrganizationMessageTypes(organizationId),
  });

  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
  const [slideoutTemplateSlug, setSlideoutTemplateSlug] = useState<string | null>(null);

  if (!NetworkRequest.isFinished(request)) {
    return <LoadingPageWrapper />;
  }

  let messageTypesToShow = messageTypes.filter(
    (messageType) => !OMITTED_EMAIL_TEMPLATE_SLUGS.includes(messageType.slug),
  );

  return (
    <StandardPageWrapper fullWidth>
      <TopNavigationBreadcrumb source="DSAR Workflows" title="Email Templates" />

      <div className="tw-boundary">
        <div className="text-[18px] font-black text-neutral-800">
          View and customize your message templates.
        </div>

        <div className="flex gap-8 items-start">
          <div className="max-w-[600px] mt-6 border border-neutral-100 rounded-[6px]">
            {messageTypesToShow
              .sort((a, b) => a.sortKey - b.sortKey)
              .map(({ slug, title }, idx) => (
                <div
                  className={clsx(
                    idx < messageTypesToShow.length && `border-b-1 border-solid border-neutral-200`,
                    `flex justify-between p-4 cursor-pointer`,
                    isSlideoutOpen && slideoutTemplateSlug === slug && "bg-primary-100",
                  )}
                  key={slug}
                  onClick={() => {
                    setIsSlideoutOpen(true);
                    setSlideoutTemplateSlug(slug);
                  }}
                >
                  <div className="text-primary-800 text-[16px] font-medium">{title}</div>
                  <div>
                    <ArrowRightCircleIcon className="w-[20px] h-[20px] text-primary-600" />
                  </div>
                </div>
              ))}
          </div>

          <div className="flex-1 mr-[64px] ml-[40px] rounded-lg bg-white shadow-[0px_2px_12px_0px_#0000001F] px-6 py-5 self-center flex items-center gap-4">
            <div>
              <p className="text-lg leading-6 font-medium text-gray-900">
                These are all the email templates used in the DSAR processing workflows.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Click on any template to view it and make changes.
              </p>
            </div>
            <svg
              width="75"
              height="60"
              viewBox="0 0 75 60"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="flex-shrink-0"
            >
              <path
                d="M72.1828 14.9838L3.8674 0.247067L2.97958 0.055999C2.52747 -0.0421967 2.05682 -0.010793 1.62176 0.1466C1.18669 0.303993 0.804885 0.580974 0.520241 0.945708C0.235597 1.31044 0.0596681 1.74811 0.0126991 2.20838C-0.0342698 2.66864 0.0496321 3.13282 0.254736 3.54754L27.3787 58.6299C27.5543 58.9838 27.8123 59.2904 28.131 59.5238C28.4497 59.7571 28.8199 59.9105 29.2103 59.971C29.6019 60.0319 30.0024 59.9966 30.3773 59.868C30.7522 59.7395 31.0902 59.5216 31.362 59.2332L50.2017 39.3683C50.3357 39.2235 50.4982 39.1079 50.6791 39.0289C50.86 38.9499 51.0552 38.9092 51.2526 38.9094C51.5208 38.9056 51.7851 38.9748 52.017 39.1097C52.2489 39.2445 52.4398 39.4399 52.5693 39.6749L56.4282 46.8126C56.6745 47.2885 57.073 47.6683 57.5602 47.8915C58.0473 48.1148 58.5952 48.1686 59.1165 48.0445L59.1357 48.0403C59.6634 47.9285 60.1381 47.6425 60.4836 47.2284C60.8292 46.8142 61.0254 46.2959 61.0408 45.7568L61.8138 29.2805C61.823 29.075 61.8743 28.8735 61.9646 28.6885C62.0616 28.5056 62.1931 28.3431 62.352 28.2103L73.2207 19.2884C73.578 18.9951 73.8454 18.6072 73.9923 18.169C74.1392 17.7307 74.1596 17.26 74.0513 16.8107C73.9429 16.3614 73.7102 15.9517 73.3797 15.6286C73.0493 15.3054 72.6344 15.082 72.1828 14.9838Z"
                fill="#F3F0FE"
              />
              <path
                d="M72.1828 14.9838L3.8674 0.247067L2.97958 0.055999C2.52747 -0.0421967 2.05682 -0.010793 1.62176 0.1466C1.18669 0.303993 0.804885 0.580974 0.520241 0.945708C0.235597 1.31044 0.0596681 1.74811 0.0126991 2.20838C-0.0342698 2.66864 0.0496321 3.13282 0.254736 3.54754L27.3787 58.6299C27.5543 58.9838 27.8123 59.2904 28.131 59.5238C28.4497 59.7571 28.8199 59.9105 29.2103 59.971C29.6019 60.0319 30.0024 59.9966 30.3773 59.868C30.7522 59.7395 31.0902 59.5216 31.362 59.2332L50.2017 39.3683C50.3357 39.2235 50.4982 39.1079 50.6791 39.0289C50.86 38.9499 51.0552 38.9092 51.2526 38.9094C51.5208 38.9056 51.7851 38.9748 52.017 39.1097C52.2489 39.2445 52.4398 39.4399 52.5693 39.6749L56.4282 46.8126C56.6745 47.2885 57.073 47.6683 57.5602 47.8915C58.0473 48.1148 58.5952 48.1686 59.1165 48.0445L59.1357 48.0403C59.6634 47.9285 60.1381 47.6426 60.4836 47.2284C60.8292 46.8142 61.0254 46.2959 61.0408 45.7568L61.8138 29.2805C61.823 29.075 61.8743 28.8735 61.9646 28.6885C62.0616 28.5056 62.1931 28.3431 62.352 28.2103L73.2207 19.2884C73.578 18.9952 73.8454 18.6072 73.9923 18.169C74.1392 17.7307 74.1596 17.26 74.0513 16.8107C73.9429 16.3614 73.7102 15.9517 73.3797 15.6286C73.0493 15.3054 72.6344 15.082 72.1828 14.9838ZM72.6001 18.5299L61.7313 27.4519C61.5897 27.566 61.4614 27.6956 61.3487 27.8384C61.2472 27.9654 61.1595 28.1029 61.0871 28.2486C61.0113 28.3976 60.9516 28.5542 60.9089 28.7158L60.91 28.7206C60.8649 28.8889 60.839 29.0618 60.8326 29.2359L60.0598 45.7121C60.0533 46.0361 59.9367 46.3482 59.7293 46.5971C59.5219 46.846 59.2359 47.017 58.9185 47.0818L58.9089 47.084C58.596 47.1617 58.2661 47.1314 57.9726 46.9981C57.6792 46.8649 57.4393 46.6363 57.2919 46.3497L53.432 39.2072C53.2331 38.8398 52.9437 38.5294 52.5911 38.3053C52.2385 38.0812 51.8346 37.9509 51.4175 37.9268L51.4128 37.9278C51.3567 37.9252 51.3008 37.9224 51.246 37.9245C50.8568 37.9247 50.4736 38.0198 50.1296 38.2017C49.8892 38.3267 49.6722 38.4922 49.4881 38.6909L30.6486 58.5556C30.4851 58.7276 30.2826 58.8576 30.0581 58.9346C29.8336 59.0116 29.5938 59.0332 29.3592 58.9977C29.1246 58.9622 28.902 58.8705 28.7104 58.7306C28.5187 58.5906 28.3637 58.4064 28.2585 58.1937L1.13449 3.11134C1.01323 2.86246 0.964309 2.58453 0.993308 2.3092C1.02231 2.03388 1.12805 1.77223 1.29851 1.55408C1.46896 1.33592 1.69726 1.17004 1.95739 1.07531C2.21753 0.980582 2.49904 0.96082 2.76986 1.01828L3.67876 1.21469L71.9728 15.9461C72.244 16.0049 72.4931 16.1387 72.6918 16.3325C72.8904 16.5262 73.0304 16.7719 73.0959 17.0415C73.1613 17.3112 73.1495 17.5938 73.0619 17.857C72.9742 18.1202 72.8141 18.3534 72.6001 18.5299Z"
                fill="#5331C6"
              />
              <path
                d="M61.567 27.9321L61.1611 28.8272L60.9103 28.7166L60.9092 28.7118L3.35004 2.61217L51.4131 37.9238L51.4178 37.9228L51.5451 38.0204L50.9656 38.8129L50.1299 38.1977L2.76583 3.40567C2.57956 3.27168 2.44598 3.07681 2.38818 2.85476C2.33038 2.63271 2.352 2.39743 2.4493 2.18963C2.5466 1.98183 2.71346 1.81457 2.92103 1.71678C3.1286 1.61898 3.36382 1.5968 3.58601 1.65407C3.64044 1.66736 3.69344 1.68596 3.74425 1.70959L61.3491 27.8344L61.567 27.9321Z"
                fill="#5331C6"
              />
            </svg>
          </div>
        </div>

        {isSlideoutOpen && slideoutTemplateSlug && (
          <Slideout
            open={isSlideoutOpen}
            setOpen={(newIsOpen: boolean) => {
              if (newIsOpen) {
                setIsSlideoutOpen(true);
              } else {
                setIsSlideoutOpen(false);
                setSlideoutTemplateSlug(null);
              }
            }}
            maxWidth="max-w-[672px]"
            slideFrom="right"
          >
            <EditEmailTemplateSidebar
              orgId={organizationId}
              templateSlug={slideoutTemplateSlug}
              closeSidebar={() => {
                setIsSlideoutOpen(false);
                setSlideoutTemplateSlug(null);
              }}
            />
          </Slideout>
        )}
      </div>
    </StandardPageWrapper>
  );
};

function EditEmailTemplateSidebar({
  orgId,
  templateSlug,
  closeSidebar,
}: {
  orgId: string;
  templateSlug: string;
  closeSidebar: () => void;
}) {
  const {
    result: sample,
    request,
    invalidate: refreshSample,
  } = useDataRequest({
    queryKey: ["emailTemplate", orgId, templateSlug],
    api: () => Api.organization.getOrganizationMessageSample(orgId, templateSlug),
  });

  const { fetch: updateCustomText } = useActionRequest({
    api: (customMessage) =>
      Api.organization.updateOrganizationMessageSample(
        orgId,
        templateSlug,
        customMessage?.trim() ?? "",
      ),
  });

  const [customText, setCustomText] = useState<string | null>(null);

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (sample && customText === null) {
      setCustomText(sample.customText);
    }
  }, [sample]);

  if (!NetworkRequest.isFinished(request)) {
    return <Loading />;
  }

  const { messageType, templateHTML } = sample;
  const isDeleteEmailVerification = templateSlug === DELETE_EMAIL_VERIFICATION_TEMPLATE_SLUG;

  let beforeHTML = "",
    afterHTML = "";
  if (templateHTML) {
    [beforeHTML, afterHTML] = templateHTML
      .split('<p class="custom-text">[[[CUSTOM_TEXT]]]</p>')
      .map((html) => (isDeleteEmailVerification ? html : html.replace(/href/gi, "xhref")));
  }

  let isCustomTextInvalid = customText && customText.length > 300;

  let saveButton = (
    <Button
      size="s"
      color="primary"
      onClick={async () => {
        setIsSaving(true);
        await updateCustomText(customText);
        await refreshSample();
        setIsSaving(false);
      }}
      disabled={isSaving || isCustomTextInvalid}
    >
      Save
    </Button>
  );

  return (
    <div className="h-full flex flex-col">
      <div className="bg-neutral-0 p-6">
        <div className="text-gray-900 text-[18px] font-bold">{messageType.title}</div>
      </div>

      <div className="p-6 flex-grow overflow-y-auto">
        <div className="text-gray-500 text-[14px] font-normal">{messageType.instructions}</div>
        {isDeleteEmailVerification && (
          <AlertCard variant="warning">
            If data is covered by an{" "}
            <a
              className="font-medium underline"
              href="https://knowledge.truevault.com/article/218-exceptions-to-deletion"
              target="_blank"
              rel="noopener noreferrer"
            >
              exception to deletion
            </a>
            , such as being necessary to fulfill a warranty, you should not tell consumers that
            submitting a request will require deletion of that data.
          </AlertCard>
        )}
        <div className="bg-neutral-0 p-2 mt-6">
          <EmailMessage
            recipient="consumer@example.com"
            subject={
              isDeleteEmailVerification
                ? "Verify Email Address for Acme Co. Request to Delete"
                : "A message about your request ABC123EFG"
            }
          >
            <div dangerouslySetInnerHTML={{ __html: beforeHTML }} />
            <textarea
              rows={4}
              className="block w-full rounded-md bg-white my-[16px] px-3 py-1.5 text-base text-neutral-800 placeholder:text-neutral-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
              defaultValue={customText && customText.length > 0 ? customText : null}
              placeholder="(optional) Add your own language here"
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                let newCustomText = e.target.value;

                setCustomText(newCustomText);
              }}
            />
            <div dangerouslySetInnerHTML={{ __html: afterHTML }} />
          </EmailMessage>
        </div>
      </div>

      <div className="h-[78px] mt-auto border-t border-gray-200 p-[20px]">
        <div className="flex justify-end gap-[16px] items-center">
          <Button size="s" color="tertiary" onClick={closeSidebar}>
            Cancel
          </Button>
          {isCustomTextInvalid ? (
            <Tooltip title="Custom Text cannot exceed 300 characters in length" placement="top">
              <div>{saveButton}</div>
            </Tooltip>
          ) : (
            saveButton
          )}
        </div>
      </div>
    </div>
  );
}
